import { useMemo, useState, useCallback } from 'react';
import { useDataStore } from '../stores/dataStore';
import { useSelectionStore } from '../stores/selectionStore';
import type { Company, Ranking } from '../types';
import DecisionBar from './skilltree/DecisionBar';

interface QuadrantChartProps {
  chain: string;
}

interface MergedRow {
  company_id: string;
  company: Company | undefined;
  ranking: Ranking;
  tqs: number;
  mcs: number;
  priority: number;
  quadrant: string;
  modeLabel: string;
  aiRevenuePct: number;
}

const QUADRANT_META: Record<string, { bg: string; label: string; textColor: string }> = {
  '核心主攻': { bg: '#dcfce7', label: '核心主攻', textColor: '#166534' },
  '质量等待': { bg: '#dbeafe', label: '质量等待', textColor: '#1e40af' },
  '弹性交易': { bg: '#ffedd5', label: '弹性交易', textColor: '#9a3412' },
  '低优先级': { bg: '#f3f4f6', label: '低优先级', textColor: '#6b7280' },
};

const MODE_COLORS: Record<string, string> = {
  A: '#3b82f6',
  B: '#8b5cf6',
  AB: '#eab308',
};

const CHART_MARGIN = { top: 30, right: 30, bottom: 50, left: 50 };

export default function QuadrantChart({ chain }: QuadrantChartProps) {
  const { rankings, companyIndex, index } = useDataStore();
  const selectedNodeId = useSelectionStore(s => s.selectedNodeId);
  const selectedCompanyId = useSelectionStore(s => s.selectedCompanyId);
  const selectCompany = useSelectionStore(s => s.selectCompany);
  const [marketFilter, setMarketFilter] = useState<'all' | 'A股' | '港股通' | '美股'>('all');
  const [chainFilter, setChainFilter] = useState<string>(chain);
  const [prevChain, setPrevChain] = useState<string>(chain);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [containerHeight, setContainerHeight] = useState(600);

  // Sync external `chain` prop into local override state during render (React 19 idiom).
  if (prevChain !== chain) {
    setPrevChain(chain);
    setChainFilter(chain);
  }

  // Responsive container size
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerWidth(Math.max(width, 320));
        setContainerHeight(Math.max(height, 400));
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const merged: MergedRow[] = useMemo(() => {
    return rankings
      .map(r => {
        const comp = companyIndex.get(r.company_id);
        return {
          company_id: r.company_id,
          company: comp,
          ranking: r,
          tqs: r.tqs_score ?? 0,
          mcs: r.mcs_score ?? 0,
          priority: r.priority_score ?? 0,
          quadrant: r.quadrant ?? '',
          modeLabel: r.mode_label ?? '',
          aiRevenuePct: comp?.ai_revenue_pct ?? 0,
        };
      })
      .filter(r => r.tqs > 0 && r.mcs > 0);
  }, [rankings, companyIndex]);

  const filtered: MergedRow[] = useMemo(() => {
    let result = merged;
    if (chainFilter !== 'all') {
      result = result.filter(r => r.company?.chains?.includes(chainFilter));
    }
    if (marketFilter === 'A股') {
      result = result.filter(r => r.company?.exchange === 'A股');
    } else if (marketFilter === '港股通') {
      result = result.filter(r => r.company?.investment_access?.hk_connect === true);
    } else if (marketFilter === '美股') {
      result = result.filter(r => r.company?.exchange === '美股');
    }
    return result;
  }, [merged, chainFilter, marketFilter]);

  // 联动高亮：选中节点时，派生关联公司集合
  const relatedCompanyIds = useMemo(() => {
    if (!selectedNodeId || !index?.nodeCompaniesMap) return null;
    const ids = index.nodeCompaniesMap[selectedNodeId];
    return ids && ids.length > 0 ? new Set(ids) : null;
  }, [selectedNodeId, index]);
  const hasSelection = !!(selectedNodeId || selectedCompanyId);

  // Chart dimensions
  const chartW = containerWidth - CHART_MARGIN.left - CHART_MARGIN.right;
  const chartH = containerHeight - CHART_MARGIN.top - CHART_MARGIN.bottom;
  const xMin = 0, xMax = 100, yMin = 0, yMax = 100;
  const midX = 60, midY = 60;

  const scaleX = (v: number) => CHART_MARGIN.left + ((v - xMin) / (xMax - xMin)) * chartW;
  const scaleY = (v: number) => CHART_MARGIN.top + chartH - ((v - yMin) / (yMax - yMin)) * chartH;

  const dotRadius = (aiPct: number) => {
    const base = 4;
    const max = 14;
    return base + (max - base) * Math.min(aiPct / 100, 1);
  };

  const dotColor = (mode: string) => MODE_COLORS[mode] ?? '#9ca3af';

  const handleMouseMove = (id: string, e: React.MouseEvent) => {
    const svgRect = (e.currentTarget as SVGElement).closest('svg')?.getBoundingClientRect();
    if (!svgRect) return;
    setHoveredId(id);
    setTooltipPos({ x: e.clientX - svgRect.left, y: e.clientY - svgRect.top });
  };

  const hoveredRow = hoveredId ? filtered.find(r => r.company_id === hoveredId) : null;

  // Quadrant stats
  const quadrantStats = useMemo(() => {
    const stats: Record<string, number> = { '核心主攻': 0, '质量等待': 0, '弹性交易': 0, '低优先级': 0 };
    for (const r of filtered) {
      if (r.quadrant && stats[r.quadrant] !== undefined) stats[r.quadrant]++;
    }
    return stats;
  }, [filtered]);

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc', color: '#1f2937' }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>市场:</span>
          {(['all', 'A股', '港股通', '美股'] as const).map(m => (
            <button key={m} onClick={() => setMarketFilter(m)} style={{
              padding: '3px 10px', borderRadius: '4px', fontSize: '12px',
              border: marketFilter === m ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: marketFilter === m ? '#eff6ff' : 'white', cursor: 'pointer',
            }}>
              {m === 'all' ? '全部' : m}
            </button>
          ))}
        </div>
        <div style={{ width: '1px', height: '18px', background: '#d1d5db' }} />
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>链路:</span>
          {(['all', 'nvidia', 'google', 'huawei'] as const).map(c => (
            <button key={c} onClick={() => setChainFilter(c)} style={{
              padding: '3px 10px', borderRadius: '4px', fontSize: '12px',
              border: chainFilter === c ? '2px solid #10b981' : '1px solid #d1d5db',
              background: chainFilter === c ? '#ecfdf5' : 'white', cursor: 'pointer',
            }}>
              {c === 'all' ? '全局' : c === 'nvidia' ? '英伟达链' : c === 'google' ? '谷歌链' : '华为链'}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280' }}>
          共 {filtered.length} 家
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', fontSize: '11px', color: '#6b7280', flexWrap: 'wrap' }}>
        <span>模式: </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: MODE_COLORS.A, display: 'inline-block' }} /> A(财报验证)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: MODE_COLORS.B, display: 'inline-block' }} /> B(稀缺爆发)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: MODE_COLORS.AB, display: 'inline-block' }} /> AB(混合)
        </span>
        <span style={{ marginLeft: '8px' }}>圆点大小 = AI营收占比</span>
      </div>

      {/* Chart */}
      <div ref={measureRef} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <svg width={containerWidth} height={containerHeight} style={{ display: 'block' }}>
          {/* Quadrant backgrounds */}
          {/* Bottom-left: 低优先级 */}
          <rect x={scaleX(xMin)} y={scaleY(midY)} width={scaleX(midX) - scaleX(xMin)} height={scaleY(yMin) - scaleY(midY)}
            fill={QUADRANT_META['低优先级'].bg} opacity={0.4} />
          {/* Bottom-right: 质量等待 */}
          <rect x={scaleX(midX)} y={scaleY(midY)} width={scaleX(xMax) - scaleX(midX)} height={scaleY(yMin) - scaleY(midY)}
            fill={QUADRANT_META['质量等待'].bg} opacity={0.4} />
          {/* Top-left: 弹性交易 */}
          <rect x={scaleX(xMin)} y={scaleY(yMax)} width={scaleX(midX) - scaleX(xMin)} height={scaleY(midY) - scaleY(yMax)}
            fill={QUADRANT_META['弹性交易'].bg} opacity={0.4} />
          {/* Top-right: 核心主攻 */}
          <rect x={scaleX(midX)} y={scaleY(yMax)} width={scaleX(xMax) - scaleX(midX)} height={scaleY(midY) - scaleY(yMax)}
            fill={QUADRANT_META['核心主攻'].bg} opacity={0.4} />

          {/* Quadrant labels */}
          <text x={scaleX(xMin) + (scaleX(midX) - scaleX(xMin)) / 2} y={scaleY(yMin) + 20}
            textAnchor="middle" fontSize="13" fill={QUADRANT_META['低优先级'].textColor} fontWeight="600">
            低优先级 ({quadrantStats['低优先级']})
          </text>
          <text x={scaleX(midX) + (scaleX(xMax) - scaleX(midX)) / 2} y={scaleY(yMin) + 20}
            textAnchor="middle" fontSize="13" fill={QUADRANT_META['质量等待'].textColor} fontWeight="600">
            质量等待 ({quadrantStats['质量等待']})
          </text>
          <text x={scaleX(xMin) + (scaleX(midX) - scaleX(xMin)) / 2} y={scaleY(yMax) + 20}
            textAnchor="middle" fontSize="13" fill={QUADRANT_META['弹性交易'].textColor} fontWeight="600">
            弹性交易 ({quadrantStats['弹性交易']})
          </text>
          <text x={scaleX(midX) + (scaleX(xMax) - scaleX(midX)) / 2} y={scaleY(yMax) + 20}
            textAnchor="middle" fontSize="13" fill={QUADRANT_META['核心主攻'].textColor} fontWeight="600">
            核心主攻 ({quadrantStats['核心主攻']})
          </text>

          {/* Midlines */}
          <line x1={scaleX(midX)} y1={scaleY(yMax)} x2={scaleX(midX)} y2={scaleY(yMin)}
            stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="6,4" />
          <line x1={scaleX(xMin)} y1={scaleY(midY)} x2={scaleX(xMax)} y2={scaleY(midY)}
            stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="6,4" />

          {/* Axes */}
          <line x1={scaleX(xMin)} y1={scaleY(yMin)} x2={scaleX(xMax)} y2={scaleY(yMin)}
            stroke="#374151" strokeWidth={1} />
          <line x1={scaleX(xMin)} y1={scaleY(yMax)} x2={scaleX(xMin)} y2={scaleY(yMin)}
            stroke="#374151" strokeWidth={1} />

          {/* Axis labels */}
          <text x={scaleX(xMin) + chartW / 2} y={containerHeight - 5}
            textAnchor="middle" fontSize="12" fill="#374151" fontWeight="600">
            TQS (质量得分) →
          </text>
          <text x={12} y={scaleY(yMin) - chartH / 2}
            textAnchor="middle" fontSize="12" fill="#374151" fontWeight="600"
            transform={`rotate(-90, 12, ${scaleY(yMin) - chartH / 2})`}>
            MCS (动量得分) →
          </text>

          {/* Tick marks */}
          {[0, 20, 40, 60, 80, 100].map(v => (
            <g key={`tick-${v}`}>
              <text x={scaleX(v)} y={scaleY(yMin) + 14} textAnchor="middle" fontSize="10" fill="#6b7280">{v}</text>
              <text x={scaleX(xMin) - 8} y={scaleY(v) + 4} textAnchor="end" fontSize="10" fill="#6b7280">{v}</text>
            </g>
          ))}

          {/* Data points */}
          {filtered.map(r => {
            const cx = scaleX(r.tqs);
            const cy = scaleY(r.mcs);
            const radius = dotRadius(r.aiRevenuePct);
            const isHovered = hoveredId === r.company_id;
            const isSelected = selectedCompanyId === r.company_id;
            const isRelated = !!(relatedCompanyIds?.has(r.company_id));
            const isHighlighted = isSelected || isRelated;
            const dimmed = hasSelection && !isHighlighted;
            return (
              <g key={r.company_id}
                onMouseEnter={(e) => handleMouseMove(r.company_id, e)}
                onMouseMove={(e) => handleMouseMove(r.company_id, e)}
                onMouseLeave={() => { setHoveredId(null); setTooltipPos(null); }}
                onClick={() => selectCompany(selectedCompanyId === r.company_id ? null : r.company_id)}
                style={{ cursor: 'pointer', opacity: dimmed ? 0.15 : 1, transition: 'opacity 0.15s' }}>
                {/* 选中态发光环 */}
                {isSelected && (
                  <circle cx={cx} cy={cy} r={radius + 6} fill="none"
                    stroke="#fbbf24" strokeWidth={1.5} opacity={0.6} />
                )}
                <circle cx={cx} cy={cy}
                  r={isSelected ? radius + 4 : isRelated ? radius + 2 : isHovered ? radius + 3 : radius}
                  fill={dotColor(r.modeLabel)}
                  fillOpacity={isHovered ? 0.9 : isHighlighted ? 0.95 : 0.7}
                  stroke={isSelected ? '#f59e0b' : isRelated ? '#fbbf24' : isHovered ? '#1f2937' : 'white'}
                  strokeWidth={isSelected ? 3 : isRelated ? 2 : isHovered ? 2 : 1} />
                {(isHovered || isSelected) && (
                  <text x={cx} y={cy - radius - 6} textAnchor="middle" fontSize="11"
                    fill={isSelected ? '#f59e0b' : '#1f2937'} fontWeight="600">
                    {r.company?.company || r.company_id}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoveredRow && tooltipPos && (
          <div style={{
            position: 'absolute',
            left: Math.min(tooltipPos.x + 12, containerWidth - 220),
            top: Math.max(tooltipPos.y - 80, 4),
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10,
            pointerEvents: 'none',
            minWidth: '180px',
          }}>
            <div style={{ fontWeight: 700, marginBottom: '6px', fontSize: '13px' }}>
              {hoveredRow.company?.company || hoveredRow.company_id}
              <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px', fontSize: '11px' }}>
                {hoveredRow.company?.ticker || ''}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
              <span style={{ color: '#6b7280' }}>TQS:</span>
              <span style={{ fontWeight: 600 }}>{hoveredRow.tqs.toFixed(1)}</span>
              <span style={{ color: '#6b7280' }}>MCS:</span>
              <span style={{ fontWeight: 600 }}>{hoveredRow.mcs.toFixed(1)}</span>
              <span style={{ color: '#6b7280' }}>综合P:</span>
              <span style={{ fontWeight: 600 }}>{hoveredRow.priority.toFixed(1)}</span>
              <span style={{ color: '#6b7280' }}>模式:</span>
              <span>{hoveredRow.modeLabel === 'A' ? 'A 财报验证' : hoveredRow.modeLabel === 'B' ? 'B 稀缺爆发' : hoveredRow.modeLabel === 'AB' ? 'AB 混合' : hoveredRow.modeLabel}</span>
              <span style={{ color: '#6b7280' }}>象限:</span>
              <span style={{ color: QUADRANT_META[hoveredRow.quadrant]?.textColor || '#6b7280', fontWeight: 600 }}>
                {hoveredRow.quadrant}
              </span>
            </div>
          </div>
        )}

        {/* 公司笔记 DecisionBar — 深色工具栏卡片（嵌入式） */}
        {selectedCompanyId && (
          <div style={{
            marginTop: 12, borderRadius: 8, padding: '8px 12px',
            background: '#0f172a', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <DecisionBar entityId={selectedCompanyId} entityType="company" />
          </div>
        )}
      </div>
    </div>
  );
}
